import type { ReactNode } from "react";
import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Where focus goes on close (e.g. back to an opener that is not a Radix Trigger). */
  onCloseAutoFocus?: (event: Event) => void;
}

/** Right-hand side panel (a Radix dialog sheet, like `ui/sheet`) styled with ds tokens: title + close, scrolling body, optional footer. */
export function SidePanel({ open, onOpenChange, title, children, footer, className, onCloseAutoFocus }: SidePanelProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ds-nav/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex w-full max-w-[420px] flex-col border-l border-ds-line bg-ds-panel font-ds text-ds-ink shadow-ds",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right motion-reduce:animate-none",
            className
          )}
        >
          <div className="flex h-14 flex-none items-center gap-3 border-b border-ds-line px-4">
            <DialogPrimitive.Title className="min-w-0 flex-1 truncate text-[15px] font-semibold">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close"
              className="grid h-8 w-8 place-items-center rounded-lg text-ds-ink2 hover:bg-ds-sunk focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto p-4 text-[13.5px]">{children}</div>
          {footer && <div className="flex flex-none justify-end gap-2 border-t border-ds-line px-4 py-3">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
